import { useEffect, useState } from "react";
import type { User } from "../../data/schemaTypes";
import { useMastersStore } from "../../data/mastersStore";
import { api } from "../../data/api";
import { Button, Input, Select } from "../../components/ui";

type Props = {
  /** null cuando es modo create — abrimos el form vacío. */
  driver: User | null;
  /** "create" abre form vacío; default es "edit" usando `driver`. */
  mode?: "edit" | "create";
  onClose: () => void;
  /**
   * Pre-asignar el chofer recién creado a este vehículo. Cuando viene,
   * el dropdown de "asignar a vehículo" queda fijo (el flow es: el
   * admin entró desde Maestros → Vehículos → "+ Nuevo chofer", así
   * que el vehículo ya está decidido). Solo aplica en modo create.
   */
  presetVehicleId?: string;
  /** Callback con el id del chofer recién creado — útil para refrescar
   *  la UI del caller (p.ej. seleccionarlo automáticamente). */
  onCreated?: (userId: string) => void;
};

// DriverEditModal — form de edición / creación de un chofer.
// Modo edit: actualiza vía mastersStore.updateUser (optimistic).
// Modo create: llama POST /api/users con role='driver' + password "password"
// (la convención del seed hasta JWT). Tras crear, llama hydrate() para
// que la lista de Choferes refresque y aparezca el nuevo.
export function DriverEditModal({
  driver,
  mode = "edit",
  onClose,
  presetVehicleId,
  onCreated,
}: Props) {
  const updateUser = useMastersStore((s) => s.updateUser);
  const hydrate = useMastersStore((s) => s.hydrate);
  const warehouses = useMastersStore((s) => s.warehouses);
  const vehicles = useMastersStore((s) => s.vehicles);

  const isCreate = mode === "create";

  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [active, setActive] = useState(true);
  // Asignación a vehículo — solo aplica al crear. El admin elige una
  // placa libre o "sin asignar"; tras crear, llamamos setVehicleDriver.
  const [vehicleId, setVehicleId] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isCreate) {
      setFullName("");
      setPhone("");
      setEmail("");
      setCode("");
      setActive(true);
      // Si el caller pre-seleccionó un vehículo (flow desde Vehículos),
      // arrancamos con él. Sino vacío y el admin elige del dropdown.
      setVehicleId(presetVehicleId ?? "");
      setError(null);
      return;
    }
    if (!driver) return;
    setFullName(driver.full_name);
    setPhone(driver.phone ?? "");
    setEmail(driver.email);
    setCode(driver.code ?? "");
    setActive(driver.active);
  }, [driver, isCreate, presetVehicleId]);

  // Vehículos disponibles: en create mostramos los sin chofer + el actual
  // del chofer si está editando (raro acá, pero defensivo).
  const availableVehicles = vehicles.filter(
    (v) => !v.current_driver_user_id || v.current_driver_user_id === driver?.id,
  );

  if (!isCreate && !driver) return null;

  async function handleSave() {
    if (isCreate) {
      if (!fullName.trim() || !email.trim()) {
        setError("Nombre y email son requeridos.");
        return;
      }
      setSaving(true);
      setError(null);
      try {
        const newUser = await api.createUser({
          role: "driver",
          full_name: fullName.trim(),
          email: email.trim(),
          phone: phone.trim(),
          code: code.trim(),
          active,
        });
        if (vehicleId) {
          await api.setVehicleDriver(vehicleId, newUser.id);
        }
        await hydrate();
        onCreated?.(newUser.id);
        onClose();
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setSaving(false);
      }
      return;
    }
    if (!driver) return;
    updateUser(driver.id, {
      full_name: fullName.trim(),
      phone: phone.trim(),
      email: email.trim(),
      code: code.trim(),
      active,
    });
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-[2000] bg-ink/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-lg shadow-card-hover w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-border">
          <h2 className="text-base font-semibold text-ink">
            {isCreate ? "Nuevo chofer" : "Editar chofer"}
          </h2>
          <button
            onClick={onClose}
            className="text-sub hover:text-ink text-xs"
          >
            cerrar ✕
          </button>
        </div>

        <div className="space-y-3">
          <Input
            label="Nombre completo"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            label="Teléfono"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+51900000000"
          />
          <Input
            label="Código (opcional)"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="DNI o código interno"
          />

          {isCreate ? (
            <>
              {presetVehicleId ? (
                // Cuando el caller pre-seleccionó un vehículo (típicamente
                // desde el drawer de Maestros → Vehículos), mostramos a
                // qué vehículo se va a asignar pero sin dropdown — el
                // flow ya decidió.
                <div className="bg-primary/5 border border-primary/20 rounded p-3">
                  <div className="text-[10px] uppercase tracking-wide text-sub mb-0.5">
                    Se asignará al vehículo
                  </div>
                  <div className="font-mono font-semibold text-ink text-sm">
                    {vehicles.find((v) => v.id === presetVehicleId)?.plate ?? "—"}
                  </div>
                </div>
              ) : (
                <>
                  <Select
                    label="Asignar a vehículo (opcional)"
                    value={vehicleId}
                    onChange={(e) => setVehicleId(e.target.value)}
                  >
                    <option value="">— Sin vehículo asignado —</option>
                    {availableVehicles.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.plate}
                        {warehouses.find((w) => w.id === v.warehouse_id)
                          ? ` · ${warehouses.find((w) => w.id === v.warehouse_id)!.name}`
                          : ""}
                      </option>
                    ))}
                  </Select>
                  <p className="text-[11px] text-sub leading-snug">
                    El chofer entrará al mobile con la placa del vehículo asignado.
                    Si lo dejás sin vehículo, asignalo después desde{" "}
                    <strong>Maestros → Vehículos</strong>.
                  </p>
                </>
              )}
            </>
          ) : (
            <Select label="Almacén asignado (vía vehículo)" disabled defaultValue="">
              <option value="">{warehouses[0]?.name ?? "Sin asignar"}</option>
            </Select>
          )}

          <label className="flex items-center gap-2 text-sm text-ink mt-2">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="accent-primary"
            />
            <span>Activo</span>
          </label>
        </div>

        {error && (
          <div className="mt-3 bg-state-failed/10 border border-state-failed/30 rounded p-2 text-xs text-state-failed">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-5">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Guardando…" : isCreate ? "Crear" : "Guardar"}
          </Button>
        </div>
      </div>
    </div>
  );
}
